import axios from 'axios'
import BASE_URL from './baseurl.js'

/**
 * Authentication API Module for IDWS
 *
 * This module handles all authentication-related API calls including:
 * - Login/Register
 * - Token management
 * - User profile management
 * - Session management
 *
 * Based on AuthController API Documentation
 */

// Auth-specific base URL (adds /auth to the base URL)
const AUTH_BASE_URL = `${BASE_URL}/auth`

// Token storage keys
const TOKEN_KEY = 'idws_auth_token'
const USER_KEY = 'idws_user_data'
const DEVICE_NAME_KEY = 'idws_device_name'
const TOKEN_EXPIRY_KEY = 'idws_token_expiry'

// Token expiration check interval (check every minute)
let tokenExpirationTimer = null

/**
 * Token Management Utilities
 */
export const TokenManager = {
  /**
   * Store authentication token with expiration time
   * @param {string} token - The access token
   * @param {number} expirationMinutes - Token expiration time in minutes (default: 60)
   */
  setToken(token, expirationMinutes = 60) {
    localStorage.setItem(TOKEN_KEY, token)

    // Calculate expiration time (current time + expiration minutes)
    const expirationTime = Date.now() + (expirationMinutes * 60 * 1000)
    localStorage.setItem(TOKEN_EXPIRY_KEY, expirationTime.toString())

    console.log('🔐 Token stored with expiration:', new Date(expirationTime).toLocaleString())

    // Start automatic expiration checking
    this.startExpirationTimer()
  },

  /**
   * Get stored authentication token
   * @returns {string|null} The stored token or null
   */
  getToken() {
    return localStorage.getItem(TOKEN_KEY)
  },

  /**
   * Remove authentication token
   */
  removeToken() {
    localStorage.removeItem(TOKEN_KEY)
  },

  /**
   * Check if token is expired
   * @returns {boolean} True if token is expired
   */
  isTokenExpired() {
    const expirationTime = localStorage.getItem(TOKEN_EXPIRY_KEY)
    if (!expirationTime) {
      return true // No expiration time means expired
    }

    const now = Date.now()
    const expiry = parseInt(expirationTime, 10)
    return now >= expiry
  },

  /**
   * Check if user is authenticated with valid (non-expired) token
   * @returns {boolean} True if token exists and is not expired
   */
  isAuthenticated() {
    const hasToken = !!this.getToken()
    if (!hasToken) return false

    // Check if token is expired
    if (this.isTokenExpired()) {
      console.log('🔐 Token expired, clearing auth data')
      this.clearAll()
      return false
    }

    return true
  },

  /**
   * Store user data
   * @param {Object} userData - User information
   */
  setUser(userData) {
    localStorage.setItem(USER_KEY, JSON.stringify(userData))
  },

  /**
   * Get stored user data
   * @returns {Object|null} User data or null
   */
  getUser() {
    const userData = localStorage.getItem(USER_KEY)
    return userData ? JSON.parse(userData) : null
  },

  /**
   * Remove user data
   */
  removeUser() {
    localStorage.removeItem(USER_KEY)
  },

  /**
   * Store device name
   * @param {string} deviceName - Device identifier
   */
  setDeviceName(deviceName) {
    localStorage.setItem(DEVICE_NAME_KEY, deviceName)
  },

  /**
   * Get device name
   * @returns {string} Device name or default
   */
  getDeviceName() {
    return localStorage.getItem(DEVICE_NAME_KEY) || 'Web Browser'
  },

  /**
   * Clear all stored auth data
   */
  clearAll() {
    this.removeToken()
    this.removeUser()
    localStorage.removeItem(DEVICE_NAME_KEY)
    localStorage.removeItem(TOKEN_EXPIRY_KEY)

    // Stop the expiration timer
    this.stopExpirationTimer()
  },

  /**
   * Start automatic token expiration checking
   */
  startExpirationTimer() {
    // Clear existing timer if any
    this.stopExpirationTimer()

    // Check token expiration every minute
    tokenExpirationTimer = setInterval(() => {
      if (this.isTokenExpired()) {
        console.log('🔐 Token expired during automatic check, logging out...')
        this.handleTokenExpiration()
      }
    }, 60000) // Check every 60 seconds

    console.log('🔐 Token expiration timer started')
  },

  /**
   * Stop automatic token expiration checking
   */
  stopExpirationTimer() {
    if (tokenExpirationTimer) {
      clearInterval(tokenExpirationTimer)
      tokenExpirationTimer = null
      console.log('🔐 Token expiration timer stopped')
    }
  },

  /**
   * Handle token expiration - clear data and redirect to login
   */
  handleTokenExpiration() {
    console.log('🔐 Handling token expiration...')

    // Clear all auth data
    this.clearAll()

    // Show notification to user
    if (window.showNotification) {
      window.showNotification('Your session has expired. Please log in again.', 'warning')
    }

    // Redirect to login page
    if (window.location.pathname !== '/' && window.location.pathname !== '/login') {
      console.log('🔐 Redirecting to login due to token expiration')
      window.location.href = '/'
    }
  },

  /**
   * Get remaining time before token expires
   * @returns {number} Minutes remaining before expiration
   */
  getTokenTimeRemaining() {
    const expirationTime = localStorage.getItem(TOKEN_EXPIRY_KEY)
    if (!expirationTime) return 0

    const now = Date.now()
    const expiry = parseInt(expirationTime, 10)
    const remainingMs = expiry - now

    return Math.max(0, Math.floor(remainingMs / (60 * 1000))) // Return minutes
  }
}

/**
 * Create axios instance with auth interceptors
 */
const authAxios = axios.create({
  baseURL: AUTH_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
})

// Request interceptor to add auth token
authAxios.interceptors.request.use(
  (config) => {
    const token = TokenManager.getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
authAxios.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    // Handle 401 Unauthorized responses
    if (error.response?.status === 401) {
      // Token might be expired or invalid
      TokenManager.clearAll()

      // Redirect to login if we're not already on login page
      if (window.location.pathname !== '/' && window.location.pathname !== '/login') {
        window.location.href = '/'
      }
    }
    return Promise.reject(error)
  }
)

/**
 * Authentication API Methods
 */
export const AuthAPI = {
  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @param {string} userData.name - Full name
   * @param {string} userData.email - Email address
   * @param {string} userData.password - Password
   * @param {string} userData.password_confirmation - Password confirmation
   * @param {string} [userData.position] - Job position
   * @param {string} [userData.department] - Department
   * @param {string} [userData.phone] - Phone number
   * @param {string} [userData.role] - User role
   * @param {string} [userData.notes] - Additional notes
   * @returns {Promise<Object>} API response with user data and token
   */
  async register(userData) {
    try {
      const response = await authAxios.post('/register', userData)

      if (response.data.success) {
        // Store token and user data with 60-minute expiration
        const { access_token, user } = response.data.data
        TokenManager.setToken(access_token, 60) // 60 minutes expiration
        TokenManager.setUser(user)
        TokenManager.setDeviceName('Web Browser')
      }

      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  /**
   * Login user
   * @param {Object} credentials - Login credentials
   * @param {string} credentials.email - Email address
   * @param {string} credentials.password - Password
   * @param {string} [credentials.device_name] - Device identifier
   * @returns {Promise<Object>} API response with user data and token
   */
  async login(credentials) {
    try {
      const loginData = {
        email: credentials.email,
        password: credentials.password,
        device_name: credentials.device_name || TokenManager.getDeviceName()
      }

      const response = await authAxios.post('/login', loginData)

      if (response.data.success) {
        // Store token and user data with 60-minute expiration
        const { access_token, user } = response.data.data
        TokenManager.setToken(access_token, 60) // 60 minutes expiration
        TokenManager.setUser(user)
        TokenManager.setDeviceName(loginData.device_name)
      }

      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  /**
   * Get current user information
   * @returns {Promise<Object>} Current user data
   */
  async getCurrentUser() {
    try {
      const response = await authAxios.get('/me')

      if (response.data.success) {
        // Update stored user data
        TokenManager.setUser(response.data.data.user)
      }

      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  /**
   * Alias for getCurrentUser to match API endpoint name
   * @returns {Promise<Object>} Current user data
   */
  async me() {
    return this.getCurrentUser()
  },

  /**
   * Update user password
   * @param {Object} passwordData - Password update data
   * @param {string} passwordData.current_password - Current password
   * @param {string} passwordData.password - New password
   * @param {string} passwordData.password_confirmation - Password confirmation
   * @returns {Promise<Object>} API response
   */
  async updatePassword(passwordData) {
    try {
      const response = await authAxios.put('/update-password', passwordData)

      // After password change, user is logged out
      if (response.data.success) {
        TokenManager.clearAll()
      }

      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  /**
   * Update user profile
   * @param {Object} profileData - Profile update data
   * @param {string} profileData.name - Full name
   * @param {string} profileData.email - Email address
   * @param {string} [profileData.position] - Job position
   * @param {string} [profileData.department] - Department
   * @param {string} [profileData.phone] - Phone number
   * @param {string} [profileData.role] - User role
   * @param {string} [profileData.notes] - Additional notes
   * @returns {Promise<Object>} API response with updated user data
   */
  async updateProfile(profileData) {
    try {
      const response = await authAxios.put('/update-profile', profileData)

      if (response.data.success) {
        // Update stored user data
        TokenManager.setUser(response.data.data.user)
      }

      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  /**
   * Logout from current session
   * @returns {Promise<Object>} API response
   */
  async logout() {
    try {
      const response = await authAxios.post('/logout')

      // Clear local storage regardless of API response
      TokenManager.clearAll()

      return response.data
    } catch (error) {
      // Clear local storage even if API call fails
      TokenManager.clearAll()
      throw this.handleError(error)
    }
  },

  /**
   * Logout from all devices
   * @returns {Promise<Object>} API response
   */
  async logoutAll() {
    try {
      const response = await authAxios.post('/logout-all')

      // Clear local storage
      TokenManager.clearAll()

      return response.data
    } catch (error) {
      // Clear local storage even if API call fails
      TokenManager.clearAll()
      throw this.handleError(error)
    }
  },

  /**
   * Get list of active tokens
   * @returns {Promise<Object>} List of active tokens
   */
  async getActiveTokens() {
    try {
      const response = await authAxios.get('/tokens')
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  /**
   * Revoke a specific token
   * @param {number} tokenId - Token ID to revoke
   * @returns {Promise<Object>} API response
   */
  async revokeToken(tokenId) {
    try {
      const response = await authAxios.delete(`/tokens/${tokenId}`)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  /**
   * Handle API errors consistently
   * @param {Object} error - Axios error object
   * @returns {Object} Formatted error object
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const { data, status } = error.response

      return {
        success: false,
        message: data.message || `HTTP Error ${status}`,
        errors: data.errors || {},
        status: status,
        data: data
      }
    } else if (error.request) {
      // Request made but no response received
      return {
        success: false,
        message: 'Network error - please check your connection',
        errors: {},
        status: 0
      }
    } else {
      // Something else happened
      return {
        success: false,
        message: error.message || 'An unexpected error occurred',
        errors: {},
        status: 0
      }
    }
  }
}

/**
 * Initialize token expiration checking when app starts
 * Call this in your main.js or App.vue
 */
export const initializeTokenExpiration = () => {
  console.log('🔐 Initializing token expiration checking...')

  // If user has a token, start the expiration timer
  if (TokenManager.getToken() && !TokenManager.isTokenExpired()) {
    TokenManager.startExpirationTimer()
    console.log('🔐 Token found, expiration timer started')
  } else if (TokenManager.getToken() && TokenManager.isTokenExpired()) {
    // Token exists but is expired, clear it
    console.log('🔐 Found expired token, clearing auth data')
    TokenManager.clearAll()
  }
}

/**
 * Auth Helper Functions
 */
export const AuthHelpers = {
  /**
   * Check if user is authenticated
   * @returns {boolean} Authentication status
   */
  isAuthenticated() {
    return TokenManager.isAuthenticated()
  },

  /**
   * Get current user from storage
   * @returns {Object|null} User data or null
   */
  getCurrentUser() {
    return TokenManager.getUser()
  },

  /**
   * Get auth token
   * @returns {string|null} Auth token or null
   */
  getToken() {
    return TokenManager.getToken()
  },

  /**
   * Clear all auth data (useful for logout)
   */
  clearAuth() {
    TokenManager.clearAll()
  },

  /**
   * Set device name for future logins
   * @param {string} deviceName - Device identifier
   */
  setDeviceName(deviceName) {
    TokenManager.setDeviceName(deviceName)
  },

  /**
   * Check if current user has specific role
   * @param {string} role - Role to check
   * @returns {boolean} True if user has role
   */
  hasRole(role) {
    const user = this.getCurrentUser()
    return user?.role === role
  },

  /**
   * Get user's full name or email as fallback
   * @returns {string} Display name
   */
  getUserDisplayName() {
    const user = this.getCurrentUser()
    return user?.name || user?.email || 'Guest'
  },

  /**
   * Get remaining time before token expires
   * @returns {number} Minutes remaining before expiration
   */
  getTokenTimeRemaining() {
    return TokenManager.getTokenTimeRemaining()
  },

  /**
   * Check if token will expire soon (within 5 minutes)
   * @returns {boolean} True if token expires within 5 minutes
   */
  isTokenExpiringSoon() {
    const remainingMinutes = this.getTokenTimeRemaining()
    return remainingMinutes > 0 && remainingMinutes <= 5
  }
}

/**
 * Default export - Main Auth API object
 */
export default AuthAPI